import styled from '@emotion/styled';
import { BlocksPreferences } from '@innovamat/glimmer-assets';
import {
  Button,
  Checkbox,
  Grid,
  Input,
  Typography,
} from '@innovamat/glimmer-components';
import { useTranslation } from 'react-i18next';
import { useEmailPreferences } from '../../hooks';
import { EmailPreferencesSkeleton } from './email-preferences-skeleton';

type EmailPreferencesProps = {
  email: string;
};

const Col = styled(Grid.Col)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`;

const Subtitle = styled(Typography.Body1)`
  color: ${({ theme }) =>
    theme.tokens.color.alias.cm.text['text-subtle'].value};
`;

const TitleWrapper = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
`;

const TitleSubscriptionsWrapper = styled(TitleWrapper)`
  flex-direction: row;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const EmailWrapper = styled.section`
  width: 100%;
`;

const SubscriptionsWrapper = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ButtonsWrapper = styled.section`
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  align-items: center;
  margin-bottom: 48px;

  @media (max-width: ${({ theme }) => theme.breakpoints.xs}) {
    flex-direction: column;
  }
`;

const ActionsWrapper = styled.section`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Description = styled(Typography.Body2)`
  color: ${({ theme }) =>
    theme.tokens.color.alias.cm.text['text-subtle'].value};
`;

const UnsubscribeAllLink = styled(Typography.Body2)`
  color: ${({ theme }) =>
    theme.tokens.color.alias.cm.text['text-accent'].value};
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    opacity: 0.8;
  }
`;

const SubscriptionContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  &:hover {
    cursor: pointer;
  }
  border: 1px solid
    ${({ theme }) => theme.tokens.color.alias.cm.border['border-subtle'].value};
  border-radius: 8px;
  padding: 16px;
`;

const ImageWrapper = styled.div`
  position: fixed;
  right: 60px;
  bottom: -10px;

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    display: none;
  }
`;

export function EmailPreferences({
  email,
}: EmailPreferencesProps): JSX.Element {
  const { t } = useTranslation();

  const {
    groupedSubscriptions,
    unsubscribeAll,
    hasSomethingChanged,
    isEverythingUnsubscribed,
    isLoading,
    isSaving,
    toggleSubscription,
    resetValues,
    handleSave,
  } = useEmailPreferences({ email });

  return (
    <Grid.Container>
      <Grid.Row justifyContent="center">
        <Col xs={6} lg={5}>
          <TitleWrapper>
            <Typography.H2> {t('settings.emailing.title')} </Typography.H2>
            <Subtitle> {t('settings.emailing.subtitle')} </Subtitle>
          </TitleWrapper>

          <EmailWrapper>
            {email && (
              <Input
                labelText={t('settings.emailing.email')}
                status="autofilled"
                value={email}
              />
            )}
          </EmailWrapper>

          <SubscriptionsWrapper>
            {isLoading ? (
              <EmailPreferencesSkeleton />
            ) : (
              groupedSubscriptions.map((group) => (
                <Group key={group.groupKey}>
                  <TitleSubscriptionsWrapper>
                    <Typography.H3>{t(group.groupTitle)}</Typography.H3>
                  </TitleSubscriptionsWrapper>
                  {group.newsletters.map((subscription) => (
                    <SubscriptionContainer
                      key={subscription.value}
                      onClick={() => {
                        toggleSubscription(subscription.value);
                      }}
                    >
                      <TextWrapper>
                        <Typography.Subtitle1>
                          {t(subscription.name)}
                        </Typography.Subtitle1>
                        <Description>{t(subscription.description)}</Description>
                      </TextWrapper>
                      <Checkbox
                        checked={subscription.active}
                        onChange={() => {
                          toggleSubscription(subscription.value);
                        }}
                      />
                    </SubscriptionContainer>
                  ))}
                </Group>
              ))
            )}
          </SubscriptionsWrapper>

          <ButtonsWrapper>
            <UnsubscribeAllLink onClick={() => unsubscribeAll()}>
              {t('settings.emailing.unsubscribe_all')}
            </UnsubscribeAllLink>
            <ActionsWrapper>
              <Button
                variant="tertiary"
                disabled={!hasSomethingChanged || isLoading || isSaving}
                onClick={() => resetValues()}
              >
                {t('settings.emailing.CTA.reset_changes')}
              </Button>
              <Button
                disabled={!hasSomethingChanged || isLoading || isSaving}
                onClick={() => handleSave()}
                loading={isSaving}
              >
                {t('settings.emailing.CTA.save')}
              </Button>
            </ActionsWrapper>
          </ButtonsWrapper>
        </Col>
      </Grid.Row>
      <ImageWrapper>
        <BlocksPreferences />
      </ImageWrapper>
    </Grid.Container>
  );
}
